import { useState, useEffect } from 'react';
import apiClient from '../api/client';
import { ENDPOINTS } from '../api/endpoints';

/**
 * Hook to fetch referral connection data for the logged-in user
 * Returns info about who referred the user and who the user has referred
 * @param {string} userAddress - User's wallet address (optional, will skip fetch if not provided)
 * @param {Object} options - Configuration options
 * @param {boolean} options.immediate - Whether to fetch immediately on mount (default: true)
 * @returns {Object} - { connections, loading, error, refetch }
 */
export const useReferralConnections = (userAddress, options = {}) => {
  const { immediate = true } = options;
  const [connections, setConnections] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchConnections = async () => {
    if (!userAddress) {
      setConnections(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await apiClient.get(
        `${ENDPOINTS.REFERRAL.CONNECTIONS}?address=${encodeURIComponent(userAddress)}`
      );
      setConnections(response?.data || null);
    } catch (err) {
      console.error('[useReferralConnections] Error fetching connections:', err);
      setError(err?.message || 'Failed to fetch referral connections');
      setConnections(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (immediate && userAddress) {
      fetchConnections();
    }
  }, [userAddress, immediate]);

  const refetch = () => {
    if (userAddress) {
      fetchConnections();
    }
  };

  return {
    connections,
    loading,
    error,
    refetch,
  };
};

export default useReferralConnections;
